import {
  faCircleQuestion,
  faTag,
  faThumbsDown,
  faThumbsUp,
} from '@fortawesome/pro-duotone-svg-icons'

import { ListingTag, ListingTagOrNull } from '../../_types/listing-tag'
import { TagDescriptor } from './types'

/**
 * Cycles through tag states in the order: null → yes → maybe → no → null.
 * The cycle order makes the chip feel like a discoverable affordance —
 * one click commits to "yes", one more downgrades to "maybe", etc.
 *
 * @param {ListingTagOrNull} current - the currently set tag, or null
 * @returns {ListingTagOrNull} the next tag in the cycle
 */
const nextTagInCycle = (current: ListingTagOrNull): ListingTagOrNull => {
  if (current === null) {
    return 'yes'
  }

  if (current === 'yes') {
    return 'maybe'
  }

  if (current === 'maybe') {
    return 'no'
  }

  return null
}

/**
 * Picks the visual descriptor (glyph + label) for a tag.
 *
 * @param {ListingTagOrNull} tag - the tag to describe
 * @returns {TagDescriptor} the glyph + label + aria label
 */
const describeTag = (tag: ListingTagOrNull): TagDescriptor => {
  if (tag === 'yes') {
    return {
      glyph: faThumbsUp,
      label: 'Yes',
      ariaLabel: 'Tagged yes — click to change',
    }
  }

  if (tag === 'maybe') {
    return {
      glyph: faCircleQuestion,
      label: 'Maybe',
      ariaLabel: 'Tagged maybe — click to change',
    }
  }

  if (tag === 'no') {
    return {
      glyph: faThumbsDown,
      label: 'No',
      ariaLabel: 'Tagged no — click to change',
    }
  }

  return {
    glyph: faTag,
    label: 'Tag it',
    ariaLabel: 'Click to tag this listing',
  }
}

/**
 * Picks the tone variant for the chip styled component.
 *
 * @param {ListingTagOrNull} tag - the tag to map
 * @returns {'untagged' | 'yes' | 'maybe' | 'no'} the variant key
 */
const resolveTagTone = (tag: ListingTagOrNull): 'untagged' | ListingTag => {
  if (tag === null) {
    return 'untagged'
  }

  return tag
}

export { describeTag, nextTagInCycle, resolveTagTone }
